import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { modalVariants, childVariants, childVariantsDown } from "shared/utils";
import "../styles/EditCertificatesModal.css";

const SKILL_LEVELS = ["Basic", "Intermediate", "Advanced"];

const EditSkillsModal = ({
  isOpen,
  onClose,
  groupData,
  onSave,
  isNewGroup,
}) => {
  const [groupTitle, setGroupTitle] = useState("");
  const [skills, setSkills] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    level: "Basic",
    icon: "bx bx-badge-check",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (isNewGroup || !groupData) {
        setGroupTitle("");
        setSkills([]);
        setIsAddingNew(false);
        setEditForm({ name: "", level: "Basic", icon: "bx bx-badge-check" });
      } else {
        setGroupTitle(groupData.groupTitle || "");
        setSkills(groupData.skills || []);
      }
      setEditingIndex(null);
      setErrors({});
    }
  }, [isOpen, groupData, isNewGroup]);

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setIsAddingNew(false);
    setEditForm({ ...skills[index] });
  };

  const handleAddNewClick = () => {
    setIsAddingNew(true);
    setEditingIndex(null);
    setEditForm({ name: "", level: "Basic", icon: "bx bx-badge-check" });
  };

  const handleSaveEdit = () => {
    if (!editForm.name.trim()) {
      setErrors({ name: "Skill name is required" });
      return;
    }

    if (!groupTitle.trim()) {
      setErrors({ groupTitle: "Group title is required" });
      return;
    }

    const newSkills = [...skills];
    newSkills[editingIndex] = { ...editForm };

    // Save to draft immediately
    onSave({
      groupTitle: groupTitle.trim(),
      skills: newSkills,
    });

    setSkills(newSkills);
    setEditingIndex(null);
    setErrors({});
  };

  const handleSaveNew = () => {
    if (!editForm.name.trim()) {
      setErrors({ name: "Skill name is required" });
      return;
    }

    if (!groupTitle.trim()) {
      setErrors({ groupTitle: "Group title is required" });
      return;
    }

    const newSkills = [...skills, { ...editForm }];

    setSkills(newSkills);
    setIsAddingNew(false);
    setEditForm({ name: "", level: "Basic", icon: "bx bx-badge-check" });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setIsAddingNew(false);
    setErrors({});
  };

  const handleDeleteSkill = (index) => {
    if (window.confirm("Delete this skill?")) {
      const newSkills = skills.filter((_, i) => i !== index);

      // Save to draft immediately if there are skills remaining
      if (newSkills.length > 0 && groupTitle.trim()) {
        onSave({
          groupTitle: groupTitle.trim(),
          skills: newSkills,
        });
      }

      setSkills(newSkills);
      if (editingIndex === index) {
        setEditingIndex(null);
      }
    }
  };

  const handleSaveGroup = () => {
    if (!groupTitle.trim()) {
      setErrors({ groupTitle: "Group title is required" });
      return;
    }

    if (skills.length === 0) {
      setErrors({ general: "At least one skill is required" });
      return;
    }

    onSave({
      groupTitle: groupTitle.trim(),
      skills: skills,
    });

    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <motion.h3
              variants={childVariants}
              initial="hidden"
              animate="visible"
              className="preview-modal-title"
            >
              {isNewGroup ? "Add New Skills Group" : "Edit Skills Group"}
            </motion.h3>

            {errors.general && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: "0.875rem",
                  marginBottom: "1rem",
                  textAlign: "center",
                }}
              >
                {errors.general}
              </div>
            )}

            {/* Group Title Input */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ marginBottom: "1.5rem" }}
            >
              <h4
                className="preview-modal-subtitle"
                style={{
                  display: "block",
                }}
              >
                Group Title *
              </h4>
              <input
                type="text"
                className="themes__form-input glass__card"
                value={groupTitle}
                onChange={(e) => {
                  setGroupTitle(e.target.value);
                  if (errors.groupTitle)
                    setErrors({ ...errors, groupTitle: null });
                }}
                placeholder="e.g., Frontend, Backend, DevOps"
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",
                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {errors.groupTitle && (
                <span style={{ color: "#ef4444", fontSize: "0.75rem" }}>
                  {errors.groupTitle}
                </span>
              )}
            </motion.div>

            {/* Skills Cards Grid - Only show if skills exist */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
            >
              <h4
                className="preview-modal-subtitle"
                style={{ marginBottom: "1rem" }}
              >
                Skills
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "0.75rem",
                  marginBottom: "1.5rem",
                }}
                className="skills-grid-responsive"
              >
                {/* Existing Skills Cards */}
                {skills.length > 0 &&
                  skills.map((skill, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="active__color-mix glass__card"
                      style={{
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        padding: "0.65rem",
                        position: "relative",
                      }}
                    >
                      {/* Edit and Delete Icons */}
                      <div
                        style={{
                          position: "absolute",
                          top: "0.35rem",
                          right: "0.35rem",
                          display: "flex",
                          gap: "0.25rem",
                        }}
                      >
                        <motion.button
                          whileHover={{ scale: 1.15 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(index);
                          }}
                          className="glass__button"
                          style={{
                            border: "none",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "var(--body-color)",
                            padding: 0,
                            background: "var(--title-color)",
                          }}
                          title="Edit skill"
                        >
                          <i
                            className="bx bx-pencil"
                            style={{ fontSize: "14px" }}
                          ></i>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.15 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSkill(index);
                          }}
                          className="glass__button"
                          style={{
                            border: "none",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "var(--body-color)",
                            padding: 0,
                            background: "var(--title-color)",
                          }}
                          title="Delete skill"
                        >
                          <i
                            className="bx bx-trash"
                            style={{ fontSize: "14px" }}
                          ></i>
                        </motion.button>
                      </div>

                      {/* Skill Icon and Name */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginBottom: "0.25rem",
                          paddingRight: "2.5rem",
                        }}
                      >
                        <i
                          className={skill.icon}
                          style={{
                            fontSize: "1.1rem",
                            color: "var(--title-color)",
                          }}
                        ></i>
                        <h5
                          style={{
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            color: "var(--title-color)",
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {skill.name}
                        </h5>
                      </div>

                      {/* Skill Level */}
                      <p
                        style={{
                          fontSize: "0.7rem",
                          color: "var(--text-color)",
                          margin: 0,
                          paddingLeft: "1.6rem",
                        }}
                      >
                        {skill.level}
                      </p>
                    </motion.div>
                  ))}

                {/* Add New Skill Card - Hide when form is open */}
                {!isAddingNew && editingIndex === null && (
                  <motion.div
                    className="glass__card"
                    variants={childVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAddNewClick}
                    style={{
                      border: "var(--border-color)",
                      borderRadius: "0.5rem",
                      padding: "0.65rem",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "65px",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="bx bx-plus"
                      style={{
                        fontSize: "1.5rem",
                        color: "var(--title-color)",
                        marginBottom: "0.15rem",
                      }}
                    ></i>
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "500",
                        color: "var(--title-color)",
                      }}
                    >
                      Add Skill
                    </span>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Edit/Add Form Below All Cards */}
            {(isAddingNew || editingIndex !== null) && (
              <motion.div
                variants={childVariantsDown}
                initial="hidden"
                animate="visible"
              >
                {" "}
                <h5
                  className="preview-modal-subtitle"
                  style={{ marginBottom: "0.5rem", fontSize: "0.95rem" }}
                >
                  {editingIndex !== null ? "Edit Skill" : "Add New Skill"}
                </h5>
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "1rem",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ position: "relative", minHeight: "3.5rem" }}>
                      <label
                        className="preview-modal-subtitle"
                        style={{
                          fontWeight: "600",
                          fontSize: "small",
                        }}
                      >
                        Skill Name *
                      </label>
                      <input
                        className="glass__card"
                        type="text"
                        value={editForm.name}
                        onChange={(e) => {
                          setEditForm({ ...editForm, name: e.target.value });
                          if (errors.name) setErrors({ ...errors, name: null });
                        }}
                        placeholder="e.g., React, Node.js"
                        style={{
                          width: "100%",
                          height: "2.5rem",
                          marginTop: "0.5rem",
                          padding: "0 1rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                      {errors.name && (
                        <span
                          style={{
                            color: "#ef4444",
                            fontSize: "0.7rem",
                            marginTop: "0.25rem",
                            display: "block",
                            position: "absolute",
                            bottom: "-1.25rem",
                          }}
                        >
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div style={{ position: "relative", minHeight: "3.5rem" }}>
                      <label
                        className="preview-modal-subtitle"
                        style={{
                          fontWeight: "600",
                          fontSize: "small",
                        }}
                      >
                        Skill Level *
                      </label>
                      <select
                        className="glass__card"
                        value={editForm.level}
                        onChange={(e) =>
                          setEditForm({ ...editForm, level: e.target.value })
                        }
                        style={{
                          width: "100%",
                          height: "2.5rem",
                          padding: "0 1rem",
                          marginTop: "0.5rem",
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          fontSize: "0.875rem",
                          backgroundColor: "var(--body-color)",
                          color: "var(--border-color)",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      >
                        {SKILL_LEVELS.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginTop: "2rem",
                    }}
                  >
                    <ButtonComponent
                      buttonTitle={
                        editingIndex !== null ? "Save Changes" : "Add Skill"
                      }
                      buttonText={
                        editingIndex !== null ? "Save Changes" : "Add Skill"
                      }
                      onClick={
                        editingIndex !== null ? handleSaveEdit : handleSaveNew
                      }
                    />
                    <ButtonComponent
                      buttonTitle="Cancel"
                      buttonText="Cancel"
                      onClick={handleCancelEdit}
                    />
                  </motion.div>
                </motion.div>{" "}
              </motion.div>
            )}

            {/* Cancel and Save Changes Buttons */}
            {editingIndex === null && !isAddingNew && (
              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "end",
                  gap: "0.75rem",
                  marginTop: "1rem",
                }}
              >
                <ButtonComponent
                  buttonTitle="Cancel"
                  buttonText="Cancel"
                  onClick={onClose}
                />
                <ButtonComponent
                  buttonTitle="Save Changes"
                  buttonText="Save Changes"
                  onClick={handleSaveGroup}
                />
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditSkillsModal;
